export type LegalMarkdownNode =
  | { type: 'h1' | 'h2' | 'h3'; text: string }
  | { type: 'p'; text: string }
  | { type: 'ul'; items: string[] };

const inlinePattern = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

export const parseLegalMarkdown = (markdown: string): LegalMarkdownNode[] => {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const nodes: LegalMarkdownNode[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];

  const flushParagraph = () => {
    const text = paragraph.join(' ').trim();
    paragraph = [];
    if (text) {
      nodes.push({ type: 'p', text });
    }
  };

  const flushList = () => {
    if (list.length > 0) {
      nodes.push({ type: 'ul', items: list });
      list = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushParagraph();
      flushList();
      continue;
    }

    if (line.startsWith('### ')) {
      flushParagraph();
      flushList();
      nodes.push({ type: 'h3', text: line.slice(4).trim() });
      continue;
    }

    if (line.startsWith('## ')) {
      flushParagraph();
      flushList();
      nodes.push({ type: 'h2', text: line.slice(3).trim() });
      continue;
    }

    if (line.startsWith('# ')) {
      flushParagraph();
      flushList();
      nodes.push({ type: 'h1', text: line.slice(2).trim() });
      continue;
    }

    if (line.startsWith('- ')) {
      flushParagraph();
      list.push(line.slice(2).trim());
      continue;
    }

    flushList();
    paragraph.push(line.trim());
  }

  flushParagraph();
  flushList();
  return nodes;
};

export const renderLegalInline = (text: string): { __html: string } => {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const html = escaped.replace(inlinePattern, (chunk) => {
    if (chunk.startsWith('**')) {
      return `<strong>${chunk.slice(2, -2)}</strong>`;
    }

    if (chunk.startsWith('`')) {
      return `<code>${chunk.slice(1, -1)}</code>`;
    }

    const link = chunk.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      return `<a href="${link[2]}" rel="noopener noreferrer">${link[1]}</a>`;
    }

    return chunk;
  });

  return { __html: html };
};
